import { useEffect, useRef, useState } from 'react';
import {
  ConnectionState as LKConnectionState,
  RoomEvent,
  Room,
  Track,
  type RemoteParticipant,
  type LocalTrack,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client';
import type { MediaProfile, RtcCredentials } from '@shared/protocol';
import { settingsFor } from './mediaProfile';

export interface RemoteMedia {
  /** LiveKit participant identity → their subscribed tracks, for the tile to
      attach. Deliberately not a MediaStream: see the note on adaptive stream. */
  tracks: Map<string, RemoteTrack[]>;
  /** Identities currently speaking, for the active-speaker outline. */
  speaking: Set<string>;
  error: string | null;
  connected: boolean;
}

/**
 * Publishes the local stream to the SFU and exposes everyone else's.
 *
 * Participant identity is the Vortex participant id, which is also what the
 * signalling roster is keyed by — so a tile can look up its tracks directly,
 * with no mapping table to keep in step.
 *
 * **Remote tracks are handed out as LiveKit tracks, not as a MediaStream.**
 * Adaptive stream decides what quality to pull for each track by measuring the
 * elements the track is attached to, and it only learns about an element
 * through `track.attach()`. Lifting `mediaStreamTrack` out and assigning it to
 * `video.srcObject` — which is what this used to do — leaves LiveKit with no
 * attached elements, so it reports every remote video as off-screen and the
 * SFU stops sending it. See VideoTile for the attach side.
 */
export function useRoomMedia(
  rtc: RtcCredentials | null,
  local: MediaStream | null,
  profile?: MediaProfile,
): RemoteMedia {
  const settings = settingsFor(profile);
  const [tracks, setTracks] = useState<Map<string, RemoteTrack[]>>(new Map());
  const [speaking, setSpeaking] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  const roomRef = useRef<Room | null>(null);
  /** Source track → what LiveKit is publishing for it, so republishing can be
      limited to what actually changed. */
  const publishedRef = useRef(new Map<MediaStreamTrack, LocalTrack>());
  /** Publishing is serialised: two quick camera toggles otherwise interleave
      an unpublish with the next publish and leave a dead track on the SFU. */
  const queueRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (!rtc) return;

    const room = new Room({
      /* 'screen' makes the layer choice account for device pixel ratio. Left
         unset, a 640-CSS-pixel tile on a HiDPI laptop asks for a 640-wide layer
         and then draws it into 1280 real pixels, which looks exactly as soft as
         it sounds. */
      adaptiveStream: { pixelDensity: 'screen' },
      // Send several qualities so the SFU can drop weaker participants to a
      // lower one instead of stalling the whole call.
      dynacast: true,
      publishDefaults: settings.publish,
    });
    roomRef.current = room;
    let cancelled = false;

    const upsert = (identity: string, track: RemoteTrack) => {
      setTracks((prev) => {
        const next = new Map(prev);
        // Replace any previous track of the same kind, or a camera swap leaves
        // the stale one attached and the tile keeps showing the old feed.
        const existing = (next.get(identity) ?? []).filter((t) => t.kind !== track.kind);
        next.set(identity, [...existing, track]);
        return next;
      });
    };

    const drop = (identity: string, track?: RemoteTrack) => {
      setTracks((prev) => {
        const existing = prev.get(identity);
        if (!existing) return prev;
        const next = new Map(prev);
        if (track) {
          const remaining = existing.filter((t) => t !== track);
          if (remaining.length === 0) next.delete(identity);
          else next.set(identity, remaining);
        } else {
          next.delete(identity);
        }
        return next;
      });
    };

    room
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio) {
          /* A ceiling on top of adaptive stream, not instead of it — the server
             takes whichever of the two is smaller. This is the receive half of
             the low-bandwidth profile: capping what we send does nothing about
             three other people arriving at 720p. */
          if (settings.maxReceive !== undefined && track.kind === Track.Kind.Video) {
            pub.setVideoQuality(settings.maxReceive);
          }
          upsert(participant.identity, track);
        }
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub, participant: RemoteParticipant) => {
        drop(participant.identity, track);
      })
      .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => drop(participant.identity))
      .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        setSpeaking(new Set(speakers.map((s) => s.identity)));
      })
      .on(RoomEvent.ConnectionStateChanged, (state) => {
        setConnected(state === LKConnectionState.Connected);
      })
      .on(RoomEvent.Disconnected, () => setConnected(false));

    (async () => {
      try {
        await room.connect(rtc.url, rtc.token);
        if (cancelled) {
          await room.disconnect();
          return;
        }
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not connect to the media server');
        }
      }
    })();

    return () => {
      cancelled = true;
      roomRef.current = null;
      publishedRef.current.clear();
      void room.disconnect();
    };
  }, [rtc, settings]);

  // Publish (and republish on a device swap) once both the room and the local
  // stream exist. Separate from connect so a camera change does not reconnect.
  useEffect(() => {
    const room = roomRef.current;
    if (!room || !connected) return;

    /* Only the difference is applied. Turning the camera off replaces the local
       stream, and unpublishing everything on that would drop the microphone for
       everyone else mid-sentence just because the camera went away. */
    const wanted = new Set(local?.getTracks() ?? []);
    const published = publishedRef.current;

    queueRef.current = queueRef.current
      .then(async () => {
        if (roomRef.current !== room) return;

        for (const [source, track] of [...published]) {
          if (wanted.has(source)) continue;
          published.delete(source);
          // false: this hook owns the source tracks and stops them itself.
          await room.localParticipant.unpublishTrack(track, false);
        }

        for (const source of wanted) {
          if (published.has(source) || source.readyState === 'ended') continue;
          /* Tell the encoder this is moving footage. A camera track usually
             gets this right on its own, but the background processor hands back
             a canvas capture, and Chrome treats an unhinted canvas as a slide —
             it holds detail and lets the frame rate collapse. */
          if (source.kind === 'video') source.contentHint = 'motion';
          const publication = await room.localParticipant.publishTrack(source, settings.publish);
          if (publication?.track) published.set(source, publication.track);
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Could not publish your camera or microphone');
      });
  }, [connected, local, settings]);

  return { tracks, speaking, error, connected };
}
